import React, { useState, useEffect } from "react";
import { SelectChangeEvent } from "@mui/material";
import getModalStyle from "../../../../util/getModalStyle";
import api from "../../../../api/api";
import createDownloadLink from "../../../../util/createDownloadLink";
import dealerRoles from "../../../../util/dealerAccounts";

function useExportSelector(
  showTab: string,
  searchTerm: string,
  searchTermFilter: string,
  isSearch?: boolean,
  showArchive?: string,
  showActive?: string,
  open?: boolean
) {
  const [modalStyle] = useState(() => getModalStyle(50, 50));

  const [abortController, setAbortController] = useState<AbortController | {}>(
    {}
  );

  const [loadingCsv, setLoadCsv] = useState(false);

  const [error, setError] = useState(false);

  const [checked, setChecked] = useState(false);

  const [includeCompleted, setIncludeCompleted] = useState(false); //for service export

  const [sort, setSort] = useState<"Descending" | "Ascending">("Descending"); //ascending or descending

  const [sortBy, setSortBy] = useState("date"); //date, quote number, grandTotal, dealer (admin)

  const [date, setDate] = useState({
    startDate: "",
    endDate: "",
  });

  const role = JSON.parse(sessionStorage.getItem("role") || JSON.stringify(""));

  const isDealer = dealerRoles.includes(role);

  var dateObj = new Date();
  var month = dateObj.getUTCMonth() + 1;
  var day = dateObj.getUTCDate();
  var year = dateObj.getUTCFullYear();

  var month2 = month.toString().length === 1 ? `0${month}` : month;
  var day2 = day.toString().length === 1 ? `0${day}` : day;

  var newdate = `${year}-${month2}-${day2}`;

  useEffect(() => {
    if (open) return;
    const abortControllerVar = new AbortController();
    setAbortController(abortControllerVar);

    setChecked(false);
    setSort("Descending");
    setIncludeCompleted(false);
    if (showTab === "Customer List") {
      setSortBy("dealer");
    } else {
      setSortBy("date");
    }
    setDate({ startDate: "", endDate: "" });
    setLoadCsv(false);
    setError(false);
    return () =>
      Object.keys(abortController).length &&
      abortController instanceof AbortController
        ? abortController.abort()
        : undefined;
  }, [open]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setDate((prevDate) => ({ ...prevDate, [name]: value }));
  };

  const handleSortClick = (sortedOrder: "Ascending" | "Descending") =>
    setSort(sortedOrder);

  const handleSortByChange = (event: SelectChangeEvent<string>) =>
    setSortBy(event.target.value);

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setChecked(checked);
  };

  const handleCheckCompleted = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setIncludeCompleted(checked);
  };

  const getExportData = async () => {
    setLoadCsv(true);
    let startDate;
    let endDate;

    if (checked) {
      startDate = new Date(date.startDate);
      const offset = startDate.getTimezoneOffset();
      startDate.setMinutes(startDate.getMinutes() + offset);

      endDate = new Date(date.endDate);
      endDate.setMinutes(endDate.getMinutes() + offset);
    }

    var params = !isSearch
      ? {
          startDate,
          endDate,
          status: showTab,
          sortDirection: sort,
          sortBy,
          filter: checked,
          showArchive,
        }
      : {
          startDate,
          endDate,
          status: showTab,
          sortDirection: sort,
          sortBy,
          filter: checked,
          searchTerm,
          searchTermFilter,
          showArchive,
        };

    const res = await api.get("/api/exportToCsv", {
      signal:
        abortController instanceof AbortController
          ? abortController.signal
          : undefined,
      params,
      responseType: "arraybuffer",
    });

    if (res.data.byteLength === 2) {
      setLoadCsv(false);
      setError(true);
      return false;
    }

    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    createDownloadLink(
      blob,
      showTab === "Orders" ? "orders.xlsx" : "quotes.xlsx"
    );

    if (res.data.error) {
      setLoadCsv(false);
      setError(true);
      return false;
    }

    setError(false);
    setLoadCsv(false);
  };

  const getSalesReportExportData = async () => {
    setLoadCsv(true);
    let startDate;
    let endDate;

    if (checked) {
      startDate = new Date(date.startDate);
      const offset = startDate.getTimezoneOffset();
      startDate.setMinutes(startDate.getMinutes() + offset);

      endDate = new Date(date.endDate);
      endDate.setMinutes(endDate.getMinutes() + offset);
    }

    var params = !isSearch
      ? {
          startDate,
          endDate,
          sortDirection: sort,
          sortBy,
          filter: checked,
        }
      : {
          startDate,
          endDate,
          sortDirection: sort,
          sortBy,
          filter: checked,
          searchTerm,
          searchTermFilter,
        };

    const res = await api.get("/api/exportSalesReports", {
      signal:
        abortController instanceof AbortController
          ? abortController.signal
          : undefined,
      params,
      responseType: "arraybuffer",
    });

    if (res.data.byteLength === 2) {
      setLoadCsv(false);
      setError(true);
      return false;
    }

    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    createDownloadLink(blob, "sales_report.xlsx");

    if (res.data.error) {
      setLoadCsv(false);
      setError(true);
      return false;
    }

    setError(false);
    setLoadCsv(false);
  };

  const handleCustomerExport = async () => {
    setLoadCsv(true);
    try {
      const params = {
        filterBy: searchTermFilter,
        searchTerm,
        activeStatus: showActive,
        sortBy,
        sortDirection: sort,
      };

      const res = await api.get("/api/exportCustomerList", {
        signal:
          abortController instanceof AbortController
            ? abortController.signal
            : undefined,
        params,
        responseType: "arraybuffer",
      });

      if (res.data.byteLength === 2) {
        setLoadCsv(false);
        setError(true);
        return false;
      }

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      createDownloadLink(blob, "customer_list.xlsx");

      if (res.data.error) {
        setLoadCsv(false);
        setError(true);
        return false;
      }

      setError(false);
      setLoadCsv(false);
    } catch (err) {
      setLoadCsv(false);
      setError(true);
      console.log(err);
    }
  };

  const handleServiceExport = async () => {
    setLoadCsv(true);
    try {
      const params = {
        searchBy: searchTermFilter,
        searchTerm,
        sortBy,
        sortDirection: sort,
        filter: checked,
        startDate: date.startDate,
        endDate: date.endDate,
        includeCompleted,
      };

      const res = await api.get("/api/exportService", {
        signal:
          abortController instanceof AbortController
            ? abortController.signal
            : undefined,
        params,
        responseType: "arraybuffer",
      });

      if (res.data.byteLength === 2) {
        setLoadCsv(false);
        setError(true);
        return false;
      }

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      createDownloadLink(blob, "service.xlsx");

      if (res.data.error) {
        setLoadCsv(false);
        setError(true);
        return false;
      }

      setError(false);
      setLoadCsv(false);
    } catch (err) {
      setLoadCsv(false);
      setError(true);
      console.log(err);
    }
  };

  return {
    handleServiceExport,
    handleCustomerExport,
    getSalesReportExportData,
    getExportData,
    handleCheckCompleted,
    handleCheck,
    handleSortByChange,
    handleSortClick,
    handleChange,
    newdate,
    isDealer,
    error,
    sort,
    sortBy,
    loadingCsv,
    modalStyle,
    includeCompleted,
    checked,
    date,
  };
}

export default useExportSelector;
